import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getNetworkConfig } from "@/lib/api";
import { executeBuy, tradeErrorMessage } from "@/lib/poolSwap";
import { PriceChart } from "@/components/exchange/PriceChart";
import {
  TradeHeader, AmountBox, OutputBox, TokenPill, QuoteError, QuoteBreakdown, QuoteRow, QuoteDivider, FeeLabel,
  PoolDetails, SubmitButton, TxLink,
} from "@/components/exchange/TradeParts";
import { Card, CardContent } from "@/components/ui/card";
import { EXCHANGE } from "@/constants/testIds";
import { useWallet } from "@/contexts/WalletContext";
import { useLiveQuote, useTradeExecution } from "@/hooks/useTrade";
import { ShoppingCart } from "lucide-react";
import { motion } from "framer-motion";
import { FADE_UP_SLOW } from "@/lib/motion";

const BuyBreakdown = ({ quote }) => (
  <QuoteBreakdown>
    <QuoteRow label="FMI Price" value={`$${quote.rate} USD`} />
    <QuoteRow label="USDT Price (INR)" value={`INR ${quote.usdtPriceInr}`} />
    <QuoteRow label={<FeeLabel percent={quote.feePercent} />} value={`${quote.feeAmount} USDT`} testId={EXCHANGE.feeAmount} />
    <QuoteRow label="Tax" value={`$${quote.taxUsd} USD`} testId={EXCHANGE.taxAmount} />
    <QuoteDivider />
    <QuoteRow strong label="Total Cost" value={`${quote.totalCost} USDT`} testId={EXCHANGE.totalCost} valueClass="text-[#D4AF37] font-display font-bold" />
    <QuoteRow strong label="You Receive" value={`${quote.outputAmount} FMI`} valueClass="text-white font-display font-bold" />
    {quote.source === "fsc-pool" && <PoolDetails quote={quote} decimals={18} token="FMI" prefix="buy" />}
  </QuoteBreakdown>
);

export default function BuyPage() {
  const [amount, setAmount] = useState("");
  const { address, getProvider, refreshBalances } = useWallet();
  const { data: config } = useQuery({
    queryKey: ["networkConfig"], queryFn: getNetworkConfig,
    refetchInterval: (q) => (q.state.data?.buyRouter?.address ? false : 3000),
  });
  const live = Boolean(config?.buyRouter?.address);
  const { quote, setQuote, quoteError, loading } = useLiveQuote("buy", amount, "USDT", "FMI");
  const trade = useTradeExecution({ address, refreshBalances, onDone: () => { setAmount(""); setQuote(null); } });

  const handleExecute = () =>
    trade.run({
      live: quote?.source === "fsc-pool",
      execute: (onStep) => executeBuy({ eip1193: getProvider(), quote, usdtAddress: config.usdt.address, onStep }),
      successMessage: `Bought ~${quote?.outputAmount} FMI for ${quote?.totalCost} USDT`,
      errorMessage: (err) => tradeErrorMessage(err, "Purchase", "gas"),
      demoMessage: "Buy order executed successfully (Demo)",
    });

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <motion.div {...FADE_UP_SLOW}>
        <TradeHeader accent="Buy" title="FMI" subtitle="Purchase FMI tokens with USDT" live={live} liveTestId="buy-live-badge" />
        <Card className="border-[#2A2411] bg-[#0A0A0A]/80 backdrop-blur-xl gold-glow">
          <CardContent className="p-5 space-y-4">
            <AmountBox label="You Pay (USDT)" amount={amount} onChange={setAmount}><TokenPill symbol="USDT" /></AmountBox>
            <OutputBox label="You Receive (FMI)" loading={loading} quote={quote}><TokenPill symbol="FMI" /></OutputBox>
            <QuoteError error={quoteError} loading={loading} />
            {quote && <BuyBreakdown quote={quote} />}
            <SubmitButton onClick={handleExecute} disabled={!quote || trade.executing || !amount} executing={trade.executing} step={trade.step}
              address={address} amount={amount} connectLabel="Connect Wallet to Buy">
              <span className="flex items-center gap-2"><ShoppingCart size={16} /> {quote?.source === "fsc-pool" ? "Buy FMI" : "Buy FMI (Demo)"}</span>
            </SubmitButton>
            <TxLink hash={trade.lastTx} testId="buy-tx-link" label="View purchase transaction" />
          </CardContent>
        </Card>
        {live && <PriceChart />}
      </motion.div>
    </div>
  );
}
